'use client';

import { useEffect, useRef, useState } from 'react';
import { EditModal } from '@/components/ui/edit-modal';
import type { Tournament } from '@/types/tournament.model';
import { tournamentToAgeRestrictionDraft } from '@/utils/tournament-age';
import { AgeRestrictionField } from '@/features/tournament-wizard/blocks';
import type { TournamentAgeRestrictionFieldsHandle } from '@/components/tournament/shared/TournamentAgeRestrictionFields';

interface EditAgeRestrictionModalProps {
  isOpen: boolean;
  onClose: () => void;
  tournament: Tournament;
  onSave: (data: { minAge: number; maxAge: number }) => Promise<boolean>;
  isLoading: boolean;
}

export function EditAgeRestrictionModal({
  isOpen,
  onClose,
  tournament,
  onSave,
  isLoading,
}: EditAgeRestrictionModalProps) {
  const [draft, setDraft] = useState(() =>
    tournamentToAgeRestrictionDraft(tournament),
  );
  const [saveEnabled, setSaveEnabled] = useState(false);
  const fieldsRef = useRef<TournamentAgeRestrictionFieldsHandle>(null);

  useEffect(() => {
    if (!isOpen) return;
    setDraft(tournamentToAgeRestrictionDraft(tournament));
  }, [isOpen, tournament.id, tournament.minAge, tournament.maxAge]);

  const handleSave = async () => {
    const p = fieldsRef.current?.getApiPayload();
    if (!p) return;
    await onSave(p);
  };

  return (
    <EditModal
      isOpen={isOpen}
      onClose={onClose}
      title="Editar restricción de edad"
      onSave={handleSave}
      isLoading={isLoading}
      disabled={!saveEnabled}
      size="large"
    >
      <div className="py-2">
        {isOpen && (
          <AgeRestrictionField
            key={`${tournament.id}-${tournament.minAge ?? 'x'}-${tournament.maxAge ?? 'y'}`}
            ref={fieldsRef}
            value={draft}
            onChange={(patch) =>
              setDraft((prev) => ({ ...prev, ...patch }))
            }
            helpText="Sin marcar las casillas se guarda sin restricción (mínimo 0, máximo 200). Podés usar una edad máxima mayor a 200 si lo necesitás."
            wrapInCard={false}
            onApiPayloadValidityChange={setSaveEnabled}
          />
        )}
      </div>
    </EditModal>
  );
}
